import { POOL_MANAGER_ABI } from "@gotts.ai/chain";
import { encodeFunctionData, parseEther } from "viem";

import { POOL_MODIFY_LIQUIDITY_TEST_ABI } from "../contracts/v4/pool-modify-liquidity-test";
import { V2_ROUTER_ABI, V3_POSITION_MANAGER_ABI } from "../lib/abis";
import {
  alignTick,
  deadline,
  getInitialSqrtPriceX96,
  MAX_UINT256,
  sortTokens,
  TICK_LOWER,
  TICK_UPPER,
  tickFromSqrtPriceX96,
  ZERO_ADDRESS,
  ZERO_BYTES32,
} from "../lib/constants";
import { approveToken } from "../lib/helpers";

import type { DeployContext } from "../deployer/deploy-utils";
import type { MockTokenAddresses, UniswapDeployment } from "../deployer/types";
import type { Address, Hex } from "viem";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SeededPool {
  protocol: "v2" | "v3" | "v4";
  tokenA: Address;
  tokenB: Address;
  /** Fee tier in basis points (only for V3/V4) */
  feeBps?: number;
}

export interface SeedLiquidityResult {
  v2Pairs: number;
  v3Pools: number;
  v4Pools: number;
  pools: SeededPool[];
  durationMs: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// V2 amounts
const V2_ETH_AMOUNT = parseEther("10");
const V2_USDC_FOR_ETH = 20_000n * 10n ** 6n; // 20k USDC (6 decimals)
const V2_USDT_FOR_ETH = 20_000n * 10n ** 6n; // 20k USDT (6 decimals)
const V2_DAI_FOR_ETH = 20_000n * 10n ** 18n; // 20k DAI (18 decimals)
const V2_STABLECOIN_AMOUNT_6 = 50_000n * 10n ** 6n; // 50k (6 decimals)
const V2_STABLECOIN_AMOUNT_18 = 50_000n * 10n ** 18n; // 50k (18 decimals)

// V3 amounts
const V3_ETH_AMOUNT = parseEther("5");
const V3_USDC_AMOUNT = 10_000n * 10n ** 6n;
const V3_USDT_AMOUNT = 10_000n * 10n ** 6n;
const V3_DAI_AMOUNT = 10_000n * 10n ** 18n;
const V3_WBTC_AMOUNT = 1n * 10n ** 8n; // 1 WBTC
const V3_STABLE_USDC = 50_000n * 10n ** 6n;
const V3_STABLE_DAI = 50_000n * 10n ** 18n;

// V4 amounts
const V4_ETH_AMOUNT = parseEther("5");
const V4_USDC_AMOUNT = 10_000n * 10n ** 6n;
const V4_DAI_AMOUNT = 10_000n * 10n ** 18n;

// V4 liquidity delta — sized to require ~2-5 ETH + reasonable stablecoin amounts.
// Full-range L requires: amount0 ≈ L/sqrt(P), amount1 ≈ L*sqrt(P).
// At WETH/USDC ~2000, L=10^14 → ~2 WETH + ~4500 USDC.
const V4_LIQUIDITY_DELTA = 10n ** 14n;

// ---------------------------------------------------------------------------
// Concentrated Liquidity Tiers (V3)
// ---------------------------------------------------------------------------

interface ConcentratedTier {
  pctWidth: number;
  capitalShare: bigint; // out of 100
}

/** Full-range gets 20% of capital; the remaining 80% is split among tiers. */
const FULL_RANGE_SHARE = 20n;

/**
 * Return concentrated tier definitions based on tick spacing.
 * Narrower tick spacings (≤10) use tighter percentage widths.
 */
function concentratedTiers(tickSpacing: number): ConcentratedTier[] {
  if (tickSpacing <= 10) {
    return [
      { pctWidth: 1, capitalShare: 35n },
      { pctWidth: 3, capitalShare: 25n },
      { pctWidth: 10, capitalShare: 20n },
    ];
  }
  return [
    { pctWidth: 2, capitalShare: 35n },
    { pctWidth: 5, capitalShare: 25n },
    { pctWidth: 15, capitalShare: 20n },
  ];
}

/** Map V3 fee units to tick spacing. */
function feeToTickSpacing(feeUnits: number): number {
  switch (feeUnits) {
    case 100:
      return 1;
    case 500:
      return 10;
    case 3000:
      return 60;
    case 10000:
      return 200;
    default:
      return 60;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Convert fee basis points to Uniswap V3 fee units (1 bps = 100).
 */
function bpsToV3Fee(feeBps: number): number {
  return feeBps * 100;
}

// ---------------------------------------------------------------------------
// Pool Definitions
// ---------------------------------------------------------------------------

/**
 * V2 pair definitions: WETH/USDC, WETH/USDT, WETH/DAI, USDC/DAI
 */
function v2PairDefs(tokens: MockTokenAddresses): {
  tokenA: Address;
  tokenB: Address;
  amountA: bigint;
  amountB: bigint;
  isWethPair: boolean;
}[] {
  return [
    {
      tokenA: tokens.weth,
      tokenB: tokens.usdc,
      amountA: V2_ETH_AMOUNT,
      amountB: V2_USDC_FOR_ETH,
      isWethPair: true,
    },
    {
      tokenA: tokens.weth,
      tokenB: tokens.usdt,
      amountA: V2_ETH_AMOUNT,
      amountB: V2_USDT_FOR_ETH,
      isWethPair: true,
    },
    {
      tokenA: tokens.weth,
      tokenB: tokens.dai,
      amountA: V2_ETH_AMOUNT,
      amountB: V2_DAI_FOR_ETH,
      isWethPair: true,
    },
    {
      tokenA: tokens.usdc,
      tokenB: tokens.dai,
      amountA: V2_STABLECOIN_AMOUNT_6,
      amountB: V2_STABLECOIN_AMOUNT_18,
      isWethPair: false,
    },
  ];
}

/**
 * V3 pool definitions: various fee tiers.
 *
 * | Pool          | Fee (bps) | Rationale                        |
 * |---------------|-----------|----------------------------------|
 * | WETH/USDC     | 5         | Most liquid stablecoin pair      |
 * | WETH/USDT     | 30        | Standard volatile pair           |
 * | WETH/DAI      | 30        | Standard volatile pair           |
 * | USDC/DAI      | 1         | Stablecoin-stablecoin            |
 * | WETH/WBTC     | 30        | Volatile-volatile pair           |
 */
function v3PoolDefs(tokens: MockTokenAddresses): {
  tokenA: Address;
  tokenB: Address;
  feeBps: number;
  amount0: bigint;
  amount1: bigint;
}[] {
  return [
    {
      tokenA: tokens.weth,
      tokenB: tokens.usdc,
      feeBps: 5,
      amount0: V3_ETH_AMOUNT,
      amount1: V3_USDC_AMOUNT,
    },
    {
      tokenA: tokens.weth,
      tokenB: tokens.usdt,
      feeBps: 30,
      amount0: V3_ETH_AMOUNT,
      amount1: V3_USDT_AMOUNT,
    },
    {
      tokenA: tokens.weth,
      tokenB: tokens.dai,
      feeBps: 30,
      amount0: V3_ETH_AMOUNT,
      amount1: V3_DAI_AMOUNT,
    },
    {
      tokenA: tokens.usdc,
      tokenB: tokens.dai,
      feeBps: 1,
      amount0: V3_STABLE_USDC,
      amount1: V3_STABLE_DAI,
    },
    {
      tokenA: tokens.weth,
      tokenB: tokens.wbtc,
      feeBps: 30,
      amount0: V3_ETH_AMOUNT,
      amount1: V3_WBTC_AMOUNT,
    },
  ];
}

/**
 * V4 pool definitions: WETH/USDC and WETH/DAI.
 */
function v4PoolDefs(tokens: MockTokenAddresses): {
  tokenA: Address;
  tokenB: Address;
  fee: number;
  tickSpacing: number;
  amount0: bigint;
  amount1: bigint;
}[] {
  return [
    {
      tokenA: tokens.weth,
      tokenB: tokens.usdc,
      fee: 3000,
      tickSpacing: 60,
      amount0: V4_ETH_AMOUNT,
      amount1: V4_USDC_AMOUNT,
    },
    {
      tokenA: tokens.weth,
      tokenB: tokens.dai,
      fee: 3000,
      tickSpacing: 60,
      amount0: V4_ETH_AMOUNT,
      amount1: V4_DAI_AMOUNT,
    },
  ];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Resolve a token address to a short symbol for action labels. */
function tokenLabel(addr: Address, tokens: MockTokenAddresses): string {
  const lower = addr.toLowerCase();
  if (lower === tokens.weth.toLowerCase()) return "WETH";
  if (lower === tokens.usdc.toLowerCase()) return "USDC";
  if (lower === tokens.usdt.toLowerCase()) return "USDT";
  if (lower === tokens.dai.toLowerCase()) return "DAI";
  if (lower === tokens.wbtc.toLowerCase()) return "WBTC";
  return addr.slice(0, 8);
}

// ---------------------------------------------------------------------------
// V2 Seeding
// ---------------------------------------------------------------------------

async function seedV2Liquidity(
  ctx: DeployContext,
  deployment: UniswapDeployment,
  tokens: MockTokenAddresses,
  onAction?: (action: string) => void | Promise<void>,
): Promise<SeededPool[]> {
  const router = deployment.v2.router02;
  const pairs = v2PairDefs(tokens);
  const pools: SeededPool[] = [];

  for (let pi = 0; pi < pairs.length; pi++) {
    const pair = pairs[pi];
    if (!pair) continue;
    await onAction?.(
      `V2 pair ${String(pi + 1)}/${String(pairs.length)}: ${tokenLabel(pair.tokenA, tokens)}/${tokenLabel(pair.tokenB, tokens)}`,
    );
    if (pair.isWethPair) {
      // WETH pairs: approve the non-WETH token, call addLiquidityETH
      const token = pair.tokenB;
      const tokenAmount = pair.amountB;

      await approveToken(ctx, token, router, MAX_UINT256);

      const data = encodeFunctionData({
        abi: V2_ROUTER_ABI,
        functionName: "addLiquidityETH",
        args: [
          token,
          tokenAmount,
          0n, // amountTokenMin
          0n, // amountETHMin
          ctx.deployer,
          deadline(),
        ],
      });
      await ctx.walletClient.sendTransaction({
        account: ctx.deployer,
        to: router,
        data: data,
        value: pair.amountA,
        chain: ctx.walletClient.chain,
      });
    } else {
      // Non-WETH pairs: approve both tokens, call addLiquidity
      await approveToken(ctx, pair.tokenA, router, MAX_UINT256);
      await approveToken(ctx, pair.tokenB, router, MAX_UINT256);

      const data = encodeFunctionData({
        abi: V2_ROUTER_ABI,
        functionName: "addLiquidity",
        args: [
          pair.tokenA,
          pair.tokenB,
          pair.amountA,
          pair.amountB,
          0n, // amountAMin
          0n, // amountBMin
          ctx.deployer,
          deadline(),
        ],
      });
      await ctx.walletClient.sendTransaction({
        account: ctx.deployer,
        to: router,
        data: data,
        chain: ctx.walletClient.chain,
      });
    }

    pools.push({
      protocol: "v2",
      tokenA: pair.tokenA,
      tokenB: pair.tokenB,
    });
  }

  return pools;
}

// ---------------------------------------------------------------------------
// V3 Seeding
// ---------------------------------------------------------------------------

async function seedV3Liquidity(
  ctx: DeployContext,
  deployment: UniswapDeployment,
  tokens: MockTokenAddresses,
  onAction?: (action: string) => void | Promise<void>,
): Promise<SeededPool[]> {
  const npm = deployment.v3.nonfungiblePositionManager;
  const poolDefs = v3PoolDefs(tokens);
  const pools: SeededPool[] = [];

  for (let pi = 0; pi < poolDefs.length; pi++) {
    const def = poolDefs[pi];
    if (!def) continue;
    await onAction?.(
      `V3 pool ${String(pi + 1)}/${String(poolDefs.length)}: creating ${tokenLabel(def.tokenA, tokens)}/${tokenLabel(def.tokenB, tokens)} ${String(def.feeBps)}bps`,
    );
    const [token0, token1] = sortTokens(def.tokenA, def.tokenB);
    const fee = bpsToV3Fee(def.feeBps);

    const sqrtPriceX96 = getInitialSqrtPriceX96(token0, token1, tokens);

    // Step 1: Create and initialize pool
    const createData = encodeFunctionData({
      abi: V3_POSITION_MANAGER_ABI,
      functionName: "createAndInitializePoolIfNecessary",
      args: [token0, token1, fee, sqrtPriceX96],
    });
    await ctx.walletClient.sendTransaction({
      account: ctx.deployer,
      to: npm,
      data: createData,
      chain: ctx.walletClient.chain,
    });

    // Step 2: Approve tokens to the NonfungiblePositionManager
    await approveToken(ctx, token0, npm, MAX_UINT256);
    await approveToken(ctx, token1, npm, MAX_UINT256);

    // Step 3: Determine amounts for token0 and token1
    // The original def has amounts in tokenA/tokenB order; map to token0/token1
    const token0IsTokenA = token0.toLowerCase() === def.tokenA.toLowerCase();
    const amount0 = token0IsTokenA ? def.amount0 : def.amount1;
    const amount1 = token0IsTokenA ? def.amount1 : def.amount0;

    // Step 4: Mint a full-range position (20% of capital)
    const fullRangeAmount0 = (amount0 * FULL_RANGE_SHARE) / 100n;
    const fullRangeAmount1 = (amount1 * FULL_RANGE_SHARE) / 100n;

    const mintData = encodeFunctionData({
      abi: V3_POSITION_MANAGER_ABI,
      functionName: "mint",
      args: [
        {
          token0,
          token1,
          fee,
          tickLower: TICK_LOWER,
          tickUpper: TICK_UPPER,
          amount0Desired: fullRangeAmount0,
          amount1Desired: fullRangeAmount1,
          amount0Min: 0n,
          amount1Min: 0n,
          recipient: ctx.deployer,
          deadline: deadline(),
        },
      ],
    });
    await ctx.walletClient.sendTransaction({
      account: ctx.deployer,
      to: npm,
      data: mintData,
      chain: ctx.walletClient.chain,
    });

    // Step 5: Concentrated positions (remaining 80% split across tiers)
    await onAction?.(
      `V3 pool ${String(pi + 1)}/${String(poolDefs.length)}: adding ${tokenLabel(def.tokenA, tokens)}/${tokenLabel(def.tokenB, tokens)} concentrated liquidity`,
    );
    const tickSpacing = feeToTickSpacing(fee);
    const centerTick = alignTick(
      tickFromSqrtPriceX96(sqrtPriceX96),
      tickSpacing,
    );
    const tiers = concentratedTiers(tickSpacing);

    for (const tier of tiers) {
      const rawOffset = tier.pctWidth * 100;
      const alignedOffset = alignTick(rawOffset, tickSpacing);
      if (alignedOffset <= 0) continue;

      const tl = centerTick - alignedOffset;
      const tu = centerTick + alignedOffset;
      const a0 = (amount0 * tier.capitalShare) / 100n;
      const a1 = (amount1 * tier.capitalShare) / 100n;

      const concMintData = encodeFunctionData({
        abi: V3_POSITION_MANAGER_ABI,
        functionName: "mint",
        args: [
          {
            token0,
            token1,
            fee,
            tickLower: tl,
            tickUpper: tu,
            amount0Desired: a0,
            amount1Desired: a1,
            amount0Min: 0n,
            amount1Min: 0n,
            recipient: ctx.deployer,
            deadline: deadline(),
          },
        ],
      });
      await ctx.walletClient.sendTransaction({
        account: ctx.deployer,
        to: npm,
        data: concMintData,
        chain: ctx.walletClient.chain,
      });
    }

    pools.push({
      protocol: "v3",
      tokenA: def.tokenA,
      tokenB: def.tokenB,
      feeBps: def.feeBps,
    });
  }

  return pools;
}

// ---------------------------------------------------------------------------
// V4 Seeding
// ---------------------------------------------------------------------------

async function seedV4Liquidity(
  ctx: DeployContext,
  deployment: UniswapDeployment,
  tokens: MockTokenAddresses,
  onAction?: (action: string) => void | Promise<void>,
): Promise<SeededPool[]> {
  const poolManager = deployment.v4.poolManager;
  const modifyLiquidityTest = deployment.v4.poolModifyLiquidityTest;
  const poolDefs = v4PoolDefs(tokens);
  const pools: SeededPool[] = [];

  for (let pi = 0; pi < poolDefs.length; pi++) {
    const def = poolDefs[pi];
    if (!def) continue;
    await onAction?.(
      `V4 pool ${String(pi + 1)}/${String(poolDefs.length)}: initializing ${tokenLabel(def.tokenA, tokens)}/${tokenLabel(def.tokenB, tokens)}`,
    );
    const [currency0, currency1] = sortTokens(def.tokenA, def.tokenB);

    const sqrtPriceX96 = getInitialSqrtPriceX96(currency0, currency1, tokens);

    const poolKey = {
      currency0,
      currency1,
      fee: def.fee,
      tickSpacing: def.tickSpacing,
      hooks: ZERO_ADDRESS,
    };

    // Step 1: Initialize the pool on PoolManager
    const initData = encodeFunctionData({
      abi: POOL_MANAGER_ABI,
      functionName: "initialize",
      args: [poolKey, sqrtPriceX96],
    });
    await ctx.walletClient.sendTransaction({
      account: ctx.deployer,
      to: poolManager,
      data: initData,
      chain: ctx.walletClient.chain,
    });

    // Step 2: Approve tokens to PoolModifyLiquidityTest
    await approveToken(ctx, currency0, modifyLiquidityTest, MAX_UINT256);
    await approveToken(ctx, currency1, modifyLiquidityTest, MAX_UINT256);

    // Step 3: Add liquidity via PoolModifyLiquidityTest
    await onAction?.(
      `V4 pool ${String(pi + 1)}/${String(poolDefs.length)}: adding ${tokenLabel(def.tokenA, tokens)}/${tokenLabel(def.tokenB, tokens)} liquidity`,
    );
    // Use the 3-arg overload: modifyLiquidity(PoolKey, ModifyLiquidityParams, bytes hookData)
    const modifyData = encodeFunctionData({
      abi: POOL_MODIFY_LIQUIDITY_TEST_ABI,
      functionName: "modifyLiquidity",
      args: [
        poolKey,
        {
          tickLower: TICK_LOWER,
          tickUpper: TICK_UPPER,
          liquidityDelta: V4_LIQUIDITY_DELTA,
          salt: ZERO_BYTES32,
        },
        "0x" as Hex, // empty hookData
      ],
    });
    await ctx.walletClient.sendTransaction({
      account: ctx.deployer,
      to: modifyLiquidityTest,
      data: modifyData,
      chain: ctx.walletClient.chain,
    });

    pools.push({
      protocol: "v4",
      tokenA: def.tokenA,
      tokenB: def.tokenB,
      feeBps: def.fee / 100, // convert V4 fee units back to bps
    });
  }

  return pools;
}

// ---------------------------------------------------------------------------
// Main Entry Point
// ---------------------------------------------------------------------------

/**
 * Seed liquidity across V2 pairs, V3 pools, and V4 pools.
 *
 * Makes real on-chain calls:
 * - V2 Router02: `addLiquidity()` / `addLiquidityETH()`
 * - V3 NonfungiblePositionManager: `createAndInitializePoolIfNecessary()` + `mint()`
 * - V4 PoolManager: `initialize()` + PoolModifyLiquidityTest `modifyLiquidity()`
 */
export async function seedLiquidity(
  ctx: DeployContext,
  deployment: UniswapDeployment,
  tokens: MockTokenAddresses,
  onAction?: (action: string) => void | Promise<void>,
): Promise<SeedLiquidityResult> {
  const start = Date.now();

  const v2Pools = await seedV2Liquidity(ctx, deployment, tokens, onAction);
  const v3Pools = await seedV3Liquidity(ctx, deployment, tokens, onAction);
  const v4Pools = await seedV4Liquidity(ctx, deployment, tokens, onAction);

  const pools: SeededPool[] = [...v2Pools, ...v3Pools, ...v4Pools];

  return {
    v2Pairs: v2Pools.length,
    v3Pools: v3Pools.length,
    v4Pools: v4Pools.length,
    pools,
    durationMs: Date.now() - start,
  };
}
